/**
 * Attachment upload with an offline-safe queue.
 * OWNED by the features slice; the editor slice imports { uploadAttachment }
 * from this exact module for paste/drop, so the export signature is pinned.
 *
 * Contract: POST the file to /api/attachments (multipart field `file`);
 * returns the vault-relative path to embed as ![[path]]. When the request
 * fails at the network level (offline, DNS, aborted), the file is queued in
 * IndexedDB (bytes stored base64 — survives every iOS Safari IDB quirk) and
 * an OfflineQueuedError is thrown carrying a provisional path the caller MAY
 * embed optimistically. The queue is flushed on module init and whenever the
 * browser fires 'online'.
 *
 * Known limitation (logged, accepted): if the server dedupes a queued upload
 * to a different name (e.g. "photo 1.png"), any note that embedded the
 * provisional path keeps pointing at the provisional name; we log a warning
 * instead of rewriting note content.
 */
import { openDB, type IDBPDatabase } from 'idb';
import { DEFAULT_ATTACHMENT_FOLDER, joinPath } from '@rbv/shared';
import { vault } from '../sync/vault.js';

const QUEUE_DB = 'rbv-attachment-queue';
const STORE = 'queue';

/** Thrown when an upload could not reach the server and was queued instead.
 * `provisionalPath` is where the file will (most likely) land once flushed;
 * callers may embed it immediately. */
export class OfflineQueuedError extends Error {
  readonly provisionalPath: string;

  constructor(provisionalPath: string) {
    super(`offline — attachment queued, will upload when back online (${provisionalPath})`);
    this.name = 'OfflineQueuedError';
    this.provisionalPath = provisionalPath;
  }
}

interface QueuedAttachment {
  name: string;
  type: string;
  /** File bytes, base64-encoded. */
  base64: string;
  folder?: string;
  provisionalPath: string;
  queuedAt: number;
}

let dbPromise: Promise<IDBPDatabase> | null = null;
function queueDb(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(QUEUE_DB, 1, {
    upgrade(db) {
      db.createObjectStore(STORE, { autoIncrement: true });
    },
  });
  return dbPromise;
}

/** Mirror of the server's attachment filename sanitizer (routes/vault.ts). */
export function sanitizeAttachmentName(name: string): string {
  return (name || 'pasted').replace(/[^\w.\- ()\[\]]+/g, '_');
}

const B64_CHUNK = 0x8000;

export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += B64_CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + B64_CHUNK));
  }
  return btoa(bin);
}

export function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** Folder the upload is headed for — used only for the provisional path (the
 * server resolves the real folder itself when none is sent). */
function intendedFolder(folder?: string): string {
  if (folder) return folder;
  try {
    return vault.settings.attachmentFolder || DEFAULT_ATTACHMENT_FOLDER;
  } catch {
    // vault not started yet (early call / tests)
    return DEFAULT_ATTACHMENT_FOLDER;
  }
}

function postAttachment(file: File, folder?: string): Promise<Response> {
  const form = new FormData();
  form.append('file', file);
  if (folder) form.append('folder', folder);
  return fetch('/api/attachments', { method: 'POST', body: form });
}

/**
 * Upload an attachment; resolves with the vault-relative path the server
 * stored it at. On network failure the file is queued and OfflineQueuedError
 * (carrying a provisional path) is thrown. On an HTTP error a plain Error is
 * thrown — the server rejected the file, retrying won't help.
 */
export async function uploadAttachment(file: File, folder?: string): Promise<string> {
  let res: Response;
  try {
    res = await postAttachment(file, folder);
  } catch {
    const provisionalPath = joinPath(intendedFolder(folder), sanitizeAttachmentName(file.name));
    try {
      const item: QueuedAttachment = {
        name: sanitizeAttachmentName(file.name),
        type: file.type || 'application/octet-stream',
        base64: await fileToBase64(file),
        folder,
        provisionalPath,
        queuedAt: Date.now(),
      };
      await (await queueDb()).add(STORE, item);
    } catch {
      throw new Error('offline, and the attachment could not be queued');
    }
    throw new OfflineQueuedError(provisionalPath);
  }
  if (!res.ok) throw new Error(`upload failed: ${res.status}`);
  const body = (await res.json()) as { path: string };
  return body.path;
}

let flushing = false;

/** Try to upload everything in the queue. Safe to call at any time; a no-op
 * while another flush is running or when there is nothing queued. */
export async function flushAttachmentQueue(): Promise<void> {
  if (flushing || typeof indexedDB === 'undefined') return;
  flushing = true;
  try {
    const db = await queueDb();
    const keys = await db.getAllKeys(STORE);
    for (const key of keys) {
      const item = (await db.get(STORE, key)) as QueuedAttachment | undefined;
      if (!item) continue;
      const file = new File([base64ToBytes(item.base64)], item.name, { type: item.type });
      let res: Response;
      try {
        res = await postAttachment(file, item.folder);
      } catch {
        return; // still offline — keep the queue, retry on the next 'online'
      }
      if (!res.ok) {
        if (res.status >= 500) return; // transient server trouble — retry later
        await db.delete(STORE, key); // permanent rejection — drop, keep moving
        console.warn(`[attachments] dropped queued upload "${item.name}" (server said ${res.status})`);
        continue;
      }
      const { path } = (await res.json()) as { path: string };
      await db.delete(STORE, key);
      if (path !== item.provisionalPath) {
        // Server deduped to a different name; embeds of the provisional path
        // are now dangling. Logged, not auto-rewritten (see module docblock).
        console.warn(
          `[attachments] queued upload "${item.name}" landed at "${path}" ` +
            `but may be embedded as "${item.provisionalPath}" — fix the embed by hand`,
        );
      }
    }
  } finally {
    flushing = false;
  }
}

// Module init: flush leftovers from a previous session, and whenever
// connectivity comes back.
if (typeof window !== 'undefined' && typeof indexedDB !== 'undefined') {
  window.addEventListener('online', () => void flushAttachmentQueue().catch(() => undefined));
  void flushAttachmentQueue().catch(() => undefined);
}
